/**
 * RTK Query API slice for supplemental documents in contracting negotiations.
 * Supports listing, uploading, downloading, and deleting supplemental documents.
 */
import { baseApi } from '@/store/api/baseApi';
import { ApiTags, SupplementalDocumentTagIds } from '@/store/api/tags';

// --- Response Types ---

export interface SupplementalDocumentResponse {
  id: number;
  fileName: string;
  uploadedByCompanyId: string;
  uploadedByCompanyName: string;
  uploadedByUserId: string;
  created: string; // ISO 8601
}

export interface SupplementalDocumentDownloadResponse {
  url: string;
  filename: string;
}

// --- Param Types ---

export interface GetSupplementalDocumentsParams {
  negotiationId: number;
  companyId: string;
}

export interface UploadSupplementalDocumentParams {
  negotiationId: number;
  file: File;
  companyId: string;
}

export interface DeleteSupplementalDocumentParams {
  documentId: number;
  companyId: string;
  negotiationId: number; // for cache invalidation tag
}

export interface GetSupplementalDocumentDownloadParams {
  documentId: number;
  companyId: string;
}

// --- API Slice ---

const BASE_URL = '/api/v1/contracting';

export const supplementalDocumentsApi = baseApi.injectEndpoints({
  endpoints: build => ({
    getSupplementalDocuments: build.query<
      SupplementalDocumentResponse[],
      GetSupplementalDocumentsParams
    >({
      query: ({ negotiationId, companyId }) => ({
        url: `${BASE_URL}/negotiations/${negotiationId}/supplemental-documents`,
        headers: {
          'X-Company-Id': companyId,
        },
      }),
      providesTags: (result, error, { negotiationId }) => [
        {
          type: ApiTags.SUPPLEMENTAL_DOCUMENTS,
          id: `${SupplementalDocumentTagIds.LIST}-${negotiationId}`,
        },
      ],
    }),

    uploadSupplementalDocument: build.mutation<
      void,
      UploadSupplementalDocumentParams
    >({
      query: ({ negotiationId, file, companyId }) => {
        const formData = new FormData();
        formData.append('file', file);

        return {
          url: `${BASE_URL}/negotiations/${negotiationId}/supplemental-documents`,
          method: 'POST',
          body: formData,
          headers: {
            'X-Company-Id': companyId,
          },
        };
      },
      invalidatesTags: (result, error, { negotiationId }) => [
        {
          type: ApiTags.SUPPLEMENTAL_DOCUMENTS,
          id: `${SupplementalDocumentTagIds.LIST}-${negotiationId}`,
        },
      ],
    }),

    deleteSupplementalDocument: build.mutation<
      void,
      DeleteSupplementalDocumentParams
    >({
      query: ({ documentId, companyId }) => ({
        url: `${BASE_URL}/supplemental-documents/${documentId}`,
        method: 'DELETE',
        headers: {
          'X-Company-Id': companyId,
        },
      }),
      invalidatesTags: (result, error, { negotiationId }) => [
        {
          type: ApiTags.SUPPLEMENTAL_DOCUMENTS,
          id: `${SupplementalDocumentTagIds.LIST}-${negotiationId}`,
        },
      ],
    }),

    getSupplementalDocumentDownload: build.query<
      SupplementalDocumentDownloadResponse,
      GetSupplementalDocumentDownloadParams
    >({
      query: ({ documentId, companyId }) => ({
        url: `${BASE_URL}/supplemental-documents/${documentId}/download`,
        headers: {
          'X-Company-Id': companyId,
        },
      }),
      keepUnusedDataFor: 0,
    }),
  }),
});

export const {
  useGetSupplementalDocumentsQuery,
  useUploadSupplementalDocumentMutation,
  useDeleteSupplementalDocumentMutation,
  useLazyGetSupplementalDocumentDownloadQuery,
} = supplementalDocumentsApi;
